import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Users } from "lucide-react";
import { api } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";

const STATUS_COLOR = {
  discussion: "bg-teal-50 text-teal",
  activated: "bg-purple/10 text-purple",
  under_review: "bg-purple/10 text-purple",
  ending_soon: "bg-amber-100 text-amber-800",
  expired: "bg-slate-100 text-ink-muted",
  archived: "bg-slate-100 text-ink-muted",
};

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function RoomRow({ room }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0"
        data-testid={`placement-room-${room.room_id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm truncate">{room.carer_name || "Carer"}</span>
          <span className={`pill text-xs ${STATUS_COLOR[room.status] || "bg-slate-100 text-ink-muted"}`}>
            {room.status_label}
          </span>
        </div>
        <p className="text-xs text-ink-soft mt-0.5">Since {formatDate(room.created_at)}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {room.arrangement_id && (
          <Link
            to={`/workspace/family-circle/arrangement/${room.room_id}`}
            className="text-sm text-purple font-medium hover:underline"
            data-testid={`placement-arrangement-link-${room.room_id}`}
          >
            Care Arrangement
          </Link>
        )}
        <Link
          to={`/care-discussion-room/${room.room_id}`}
          className="text-sm text-teal font-medium hover:underline inline-flex items-center gap-1"
          data-testid={`placement-open-room-link-${room.room_id}`}
        >
          Open room <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </li>
  );
}

function PlacementCard({ placement }) {
  const params = new URLSearchParams({ placement_id: placement.placement_id, client_label: placement.label });
  return (
    <div className="card-base p-6 md:p-7" data-testid={`placement-card-${placement.placement_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-purple" strokeWidth={1.75} />
            {placement.label}
          </h3>
          <p className="text-sm text-ink-muted mt-0.5">
            {placement.rooms.length} Care Discussion Room{placement.rooms.length === 1 ? "" : "s"}
          </p>
        </div>
        <Link
          to={`/care-discussion-room/new?${params.toString()}`}
          className="btn-outline text-sm"
          data-testid={`placement-start-new-room-${placement.placement_id}`}
        >
          <Plus className="w-4 h-4" /> Start a separate room with another carer
        </Link>
      </div>
      <div className="mt-3">
        <Link
          to={`/workspace/family-circle/placement/${placement.placement_id}/getting-ready`}
          className="text-sm text-purple font-medium hover:underline inline-flex items-center gap-1"
          data-testid={`placement-getting-ready-link-${placement.placement_id}`}
        >
          Getting Ready for Care <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <p className="mt-2 text-xs text-ink-soft max-w-xl">
        Each carer has their own private Discussion Room and Care Arrangement.
      </p>
      <ul className="mt-4">
        {placement.rooms.map((r) => <RoomRow key={r.room_id} room={r} />)}
      </ul>
    </div>
  );
}

export default function PlacementDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/care-rooms/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData({ placements: [], earlier_rooms: [] }))
      .finally(() => setLoading(false));
  }, []);

  const placements = data?.placements || [];
  const earlierRooms = data?.earlier_rooms || [];
  const isEmpty = placements.length === 0 && earlierRooms.length === 0;

  return (
    <div data-testid="placement-dashboard-page">
      <PageHero
        eyebrow="Family Care Office"
        title="Your Care Placements"
        description="Each person receiving care, and every carer connection for them, grouped in one place."
      >
        <div className="mt-6">
          <Link to="/care-discussion-room/new" className="btn-teal" data-testid="placement-dashboard-new-room">
            <Plus className="w-4 h-4" /> Create a new Care Discussion Room
          </Link>
        </div>
      </PageHero>

      <section className="section pt-12">
        <div className="container-jcc max-w-3xl space-y-8">
          {loading ? (
            <div className="py-20 text-center text-ink-muted" data-testid="placement-dashboard-loading">Loading…</div>
          ) : isEmpty ? (
            <div className="card-base p-8 text-center" data-testid="placement-dashboard-empty">
              <h3 className="font-display text-xl font-semibold">No care connections yet</h3>
              <p className="mt-2 text-ink-muted text-sm">
                Create your first Care Discussion Room to start an agreement conversation.
              </p>
              <Link to="/care-discussion-room/new" className="btn-teal mt-5 inline-flex">
                <Plus className="w-4 h-4" /> Create a room
              </Link>
            </div>
          ) : (
            <>
              {placements.map((p) => <PlacementCard key={p.placement_id} placement={p} />)}

              {earlierRooms.length > 0 && (
                <div data-testid="earlier-rooms-section">
                  <h3 className="font-display text-lg font-semibold mb-1">Earlier Care Rooms</h3>
                  <p className="text-sm text-ink-muted mb-4">
                    Created before we started grouping rooms by the person receiving care.
                  </p>
                  <div className="card-base p-6 md:p-7">
                    <ul>
                      {earlierRooms.map((r) => <RoomRow key={r.room_id} room={r} />)}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
